import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Send, MessageCircle, User, Building2, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function MessageModal({ 
  isOpen, 
  onClose, 
  requestId, 
  studentName, 
  clinicalSiteName,
  requestShortId 
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    if (isOpen && requestId) {
      fetchMessages();
    }
  }, [isOpen, requestId]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/placement-requests/${requestId}/messages`);
      setMessages(res.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await api.post(`/placement-requests/${requestId}/messages`, {
        content: newMessage.trim()
      });
      setMessages([...messages, res.data]);
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRoleIcon = (role) => {
    if (role === "university") return <User className="w-3 h-3" />;
    if (role === "clinical_site") return <Building2 className="w-3 h-3" />;
    return <User className="w-3 h-3" />;
  };

  const getRoleBadgeColor = (role) => {
    if (role === "university") return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    if (role === "clinical_site") return "bg-cyan-500/10 text-cyan-400 border-cyan-500/20";
    return "bg-slate-500/10 text-slate-400 border-slate-500/20";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#151A23] border-slate-700 max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader className="border-b border-slate-700 pb-4">
          <DialogTitle className="text-slate-100 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-cyan-400" />
            Messages
          </DialogTitle>
          <div className="text-sm text-slate-400 mt-1">
            <span className="font-mono text-xs bg-slate-800 px-1.5 py-0.5 rounded mr-2">
              {requestShortId}
            </span>
            {studentName} → {clinicalSiteName}
          </div>
        </DialogHeader>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto min-h-[300px] max-h-[400px] py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <MessageCircle className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs mt-1">Start a conversation about this placement request</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_id === currentUser.id;
              return (
                <div
                  key={msg.id}
                  className={`flex flex-col ${isOwn ? "items-end" : "items-start"}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-[10px] px-1.5 py-0 ${getRoleBadgeColor(msg.sender_role)}`}>
                      {getRoleIcon(msg.sender_role)}
                      <span className="ml-1 capitalize">
                        {msg.sender_role === "clinical_site" ? "Clinical Site" : msg.sender_role}
                      </span>
                    </Badge>
                    <span className="text-xs text-slate-500">{msg.sender_name}</span>
                  </div>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                      isOwn
                        ? "bg-cyan-500/20 text-slate-100 rounded-br-sm"
                        : "bg-slate-800 text-slate-200 rounded-bl-sm"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  <span className="text-[10px] text-slate-500 mt-1 px-1">
                    {formatTime(msg.created_at)}
                  </span>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <form onSubmit={handleSend} className="border-t border-slate-700 pt-4">
          <div className="flex gap-2">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500 resize-none min-h-[60px] max-h-[120px]"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              data-testid="message-input"
            />
            <Button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 self-end"
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Hook to get unread message count for a request
export function useUnreadMessageCount(requestId) {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!requestId) return;
    
    const fetchCount = async () => {
      try {
        const res = await api.get(`/placement-requests/${requestId}/messages/unread-count`);
        setUnreadCount(res.data.unread_count);
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    };

    fetchCount();
    // Optionally poll for updates
    const interval = setInterval(fetchCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [requestId]);

  return unreadCount;
}
